import React from 'react'
import Button from '@components/Button'
import { Avatar, ListItem, ListItemText, Paper, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { HiChevronDoubleRight } from 'react-icons/hi2'
import { PlugToken } from '@plug-core/interfaces/common'
import { ReviewData } from '@plug-core/interfaces/staking'

export const StakeReview = ({
  data,
  amount,
  fee,
  mode,
  isLoading,
  icpToken,
  nicpToken,
  submit,
  close,
}: {
  data: ReviewData
  amount: string
  fee: number
  mode: 'stake' | 'unstake'
  isLoading: boolean
  icpToken: PlugToken
  nicpToken: PlugToken
  submit: (action: () => Promise<void>) => void
  close: () => void
}) => {
  const fromToken = mode === 'stake' ? icpToken : nicpToken
  const toToken = mode === 'unstake' ? icpToken : nicpToken

  const { t } = useTranslation()

  return (
    <Stack>
      <Stack p={2}>
        <Typography variant="h6" textAlign="center">
          {t(`stake.review.title.${mode}`)}
        </Typography>

        <Stack direction="row" justifyContent="center" alignItems="center" mt={2}>
          <Stack component={Paper} flex={1} alignItems="center" spacing={1.5} p={2} height="100%">
            <Avatar src={fromToken.logo} />
            <Stack spacing={0.5}>
              <Typography variant="h3" textAlign="center" width={100} noWrap>
                {amount}
              </Typography>
              <Typography variant="caption" textAlign="center">
                {fromToken.symbol}
              </Typography>
            </Stack>
          </Stack>

          <Stack
            position="absolute"
            justifyContent="center"
            p={1}
            bgcolor={(theme) => theme.palette.background.default}
            borderRadius={10}
          >
            <HiChevronDoubleRight color="primary.main" size={30} />
          </Stack>

          <Stack
            component={Paper}
            flex={1}
            alignItems="center"
            spacing={1.5}
            p={2}
            ml={1}
            height="100%"
          >
            <Avatar src={toToken.logo} />
            <Stack spacing={0.5}>
              <Typography variant="h3" textAlign="center" width={100} noWrap>
                {data.toAmount}
              </Typography>
              <Typography variant="caption" textAlign="center">
                {toToken.symbol}
              </Typography>
            </Stack>
          </Stack>
        </Stack>

        <Stack spacing={1} mt={1}>
          {Object.entries({
            ...data.attrs,
            [t('stake.review.fee')]: t('stake.review.feeAmount', {
              fee: fee * 100,
            }),
          }).map(([attr, value]) => (
            <ListItem key={attr}>
              <ListItemText primary={attr} />
              <ListItemText secondary={value} sx={{ textAlign: 'right' }} />
            </ListItem>
          ))}
        </Stack>
      </Stack>

      <Stack p={2} spacing={1} bgcolor="background.mid">
        <Button
          fullWidth
          variant="rainbow"
          loading={isLoading}
          onClick={() => submit(data.action)}
          disabled={isLoading}
        >
          {t(`stake.review.${mode}`)}
        </Button>

        <Button fullWidth onClick={close} disabled={isLoading}>
          {t('common.cancel')}
        </Button>
      </Stack>
    </Stack>
  )
}
